import type { ConsentAuditRecord, CustomerProfile } from '@emazy/shared-types';

import type { AuthService } from '@/features/auth/services';

import { CustomerProfileApiError, type CustomerProfileApi } from './customerProfileApi';

export class MockCustomerProfileApi implements CustomerProfileApi {
  private profile: CustomerProfile | undefined;
  private consents: ConsentAuditRecord[] = [];
  private pendingEmail: string | undefined;

  constructor(private readonly auth: AuthService) {}

  private async currentProfile() {
    const user = (await this.auth.getCurrentSession())?.user;
    if (!user) throw new CustomerProfileApiError('Sign in to continue.', 'unauthenticated', 401);
    if (!this.profile || this.profile.userId !== user.id) {
      const now = new Date().toISOString();
      this.profile = {
        userId: user.id,
        email: user.email,
        emailVerified: user.emailVerified,
        firstName: user.firstName,
        lastName: user.lastName,
        displayName: user.displayName,
        locale: 'en-US',
        communicationPreferences: {
          transactionalEmail: true,
          productUpdatesEmail: false,
          promotionsEmail: false,
        },
        profileStatus: 'active',
        version: 1,
        createdAt: now,
        updatedAt: now,
      };
      this.consents = [];
    }
    return this.profile;
  }

  private conflict(expectedVersion: number, profile: CustomerProfile) {
    if (expectedVersion !== profile.version)
      throw new CustomerProfileApiError(
        'Your account information changed elsewhere.',
        'profile-conflict',
        409,
      );
  }

  async getCustomerProfile() {
    const profile = await this.currentProfile();
    return { profile, consentSetupRequired: this.consents.length === 0 };
  }

  async updateCustomerProfile(input: Parameters<CustomerProfileApi['updateCustomerProfile']>[0]) {
    const profile = await this.currentProfile();
    this.conflict(input.expectedVersion, profile);
    this.profile = {
      ...profile,
      firstName: input.firstName.trim(),
      lastName: input.lastName.trim(),
      displayName: `${input.firstName.trim()} ${input.lastName.trim()}`,
      locale: input.locale,
      version: profile.version + 1,
      updatedAt: new Date().toISOString(),
    };
    return this.profile;
  }

  async updateCommunicationPreferences(
    input: Parameters<CustomerProfileApi['updateCommunicationPreferences']>[0],
  ) {
    const profile = await this.currentProfile();
    this.conflict(input.expectedVersion, profile);
    const now = new Date().toISOString();
    const changes = [
      [
        'product-updates-email',
        profile.communicationPreferences.productUpdatesEmail,
        input.productUpdatesEmail,
      ],
      ['marketing-email', profile.communicationPreferences.promotionsEmail, input.promotionsEmail],
    ] as const;
    for (const [consentType, before, after] of changes) {
      if (before !== after)
        this.consents.unshift({
          id: crypto.randomUUID(),
          userId: profile.userId,
          consentType,
          action: after ? 'granted' : 'withdrawn',
          policyVersion: input.policyVersion,
          occurredAt: now,
          source: 'account-settings',
        });
    }
    this.profile = {
      ...profile,
      communicationPreferences: {
        transactionalEmail: true,
        productUpdatesEmail: input.productUpdatesEmail,
        promotionsEmail: input.promotionsEmail,
      },
      version: profile.version + 1,
      updatedAt: now,
    };
    return this.profile;
  }

  async initializeConsents(input: Parameters<CustomerProfileApi['initializeConsents']>[0]) {
    const profile = await this.currentProfile();
    if (this.consents.some((record) => record.consentType === 'terms')) return;
    const now = new Date().toISOString();
    this.consents.push(
      {
        id: crypto.randomUUID(),
        userId: profile.userId,
        consentType: 'terms',
        action: 'acknowledged',
        policyVersion: input.termsPolicyVersion,
        occurredAt: now,
        source: 'system-migration',
      },
      {
        id: crypto.randomUUID(),
        userId: profile.userId,
        consentType: 'privacy',
        action: 'acknowledged',
        policyVersion: input.privacyPolicyVersion,
        occurredAt: now,
        source: 'system-migration',
      },
    );
  }

  async getConsentHistory(params: Parameters<CustomerProfileApi['getConsentHistory']>[0] = {}) {
    await this.currentProfile();
    return { items: this.consents.slice(0, params.limit ?? 20), nextCursor: null };
  }

  async startEmailChange(input: Parameters<CustomerProfileApi['startEmailChange']>[0]) {
    const profile = await this.currentProfile();
    const email = input.newEmail.trim().toLowerCase();
    if (email === profile.email)
      throw new CustomerProfileApiError('Enter a different email address.', 'same-email', 400);
    this.pendingEmail = email;
    return { status: 'verification-required' as const };
  }

  async confirmEmailChange(input: Parameters<CustomerProfileApi['confirmEmailChange']>[0]) {
    const profile = await this.currentProfile();
    if (input.code !== '123456')
      throw new CustomerProfileApiError(
        'That verification code is not valid.',
        'invalid-verification-code',
        400,
      );
    if (input.newEmail.trim().toLowerCase() !== this.pendingEmail)
      throw new CustomerProfileApiError(
        'Start the email change again.',
        'invalid-verification-code',
        400,
      );
    this.profile = {
      ...profile,
      email: this.pendingEmail,
      emailVerified: true,
      version: profile.version + 1,
      updatedAt: new Date().toISOString(),
    };
    this.pendingEmail = undefined;
    return { status: 'completed' as const, profile: this.profile };
  }
}
